import React, { useEffect, useReducer, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import styles from "../Aibox/AiboxForm.module.css"
import { Button } from 'primereact/button';
import PasswordInput from '../../../../Inputs/PasswordInput';
import Lottie from 'lottie-react';
import fail from '../../../../../../assets/animations/fail.json'
import success from '../../../../../../assets/animations/success.json'
import { hikvisionLogin, unlinkHikvision, addHikvision } from '../../../../../../Api Methods/Api';
import SelectableListRow from '../SelectableListRow';

const TextReducer = (state, action) => {
    if (action.type === 'USER_INPUT') {
        return { value: action.val, isValid: action.val.length > 0 };
    }

    return state;
}

export const EVENT_NAME_MAP = {
    "IO": "Alarm Input (Physical)",
    "linedetection": "Line Crossing",
    "fielddetection": "Intrusion Detection",
    "VMD": "Motion Detection",
    "behaviorRule": "Thermal Perimeter Rules",
    "regionentrance": "Region Entrance",
    "regionexiting": "Region Exiting"
}

// Hidden for now - fire/temperature events aren't exposed in the picker yet, though
// arm/disarm still works if an older pin already has them configured.
const HIDDEN_EVENT_TYPES = new Set(["thermometry", "tmpa", "temperature", "temperatureintervalmeasurement"])

// extraField2 holds either the array of event objects that was sent as `eventsToHandle`,
// or (for a relay/alarm-output pin) a single-element array of `{ controlLevel: "alarm_output", outputIds }`.
const parseExtraField2 = (integration) => {
    try {
        return integration?.extraField2 ? JSON.parse(integration.extraField2) : [];
    } catch {
        return [];
    }
}

const isRelayConfig = (parsed) => Array.isArray(parsed) && parsed[0]?.controlLevel === "alarm_output"

const getControlledEventTypes = (integration) => {
    const parsed = parseExtraField2(integration);
    if (isRelayConfig(parsed)) return new Set();
    const events = Array.isArray(parsed) ? parsed : (parsed?.eventsToHandle ?? []);
    return new Set(events.map(event => event?.eventType ?? event));
}

const getControlledOutputIds = (integration) => {
    const parsed = parseExtraField2(integration);
    if (!isRelayConfig(parsed)) return new Set();
    return new Set((parsed[0]?.outputIds ?? []).map(String));
}

// extraField3 holds the array of channel ids that was sent as `channelIds` when the
// integration was created/last updated. Not used for relay pins.
const getControlledChannelIds = (integration) => {
    try {
        const parsed = integration?.extraField3 ? JSON.parse(integration.extraField3) : [];
        const channelIds = Array.isArray(parsed) ? parsed : (parsed?.channelIds ?? []);
        return new Set(channelIds.map(channel => channel?.id ?? channel));
    } catch {
        return new Set();
    }
}

const HikvisionEditForm = ({ integration, apiData, siteUUID, pin, onClose, refreshDevices }) => {
    const dispatch = useDispatch();
    const userID = useSelector(state => state.auth.userID);

    const [existingIP, existingPort] = (integration?.extraField1 ?? '').split(':');
    const [username, setUsername] = useState(integration?.username ?? '')
    const [password, setPassword] = useState(integration?.password ?? '')
    const controlledEventTypes = getControlledEventTypes(integration);
    const controlledChannelIds = getControlledChannelIds(integration);
    const controlledOutputIds = getControlledOutputIds(integration);

    const [step, setStep] = useState('form');
    const [failReason, setFailReason] = useState("");

    const [controlMode, setControlMode] = useState(isRelayConfig(parseExtraField2(integration)) ? 'relay' : 'events');

    const [availableEvents, setAvailableEvents] = useState([]);
    const [selectedEvents, setSelectedEvents] = useState([]);
    const [availableChannels, setAvailableChannels] = useState([]);
    const [selectedChannels, setSelectedChannels] = useState([]);
    const [availableThermalChannels, setAvailableThermalChannels] = useState([]);
    const [availableIOOutputs, setAvailableIOOutputs] = useState([]);
    const [selectedOutputIds, setSelectedOutputIds] = useState([]);
    const [eventsLoading, setEventsLoading] = useState(true);
    const [eventsError, setEventsError] = useState("");

    const handleEventSelected = (event) => {
        setSelectedEvents((prevSelectedEvents) => {
            if (prevSelectedEvents.includes(event)) {
                return prevSelectedEvents.filter(
                    (selectedEvent) => selectedEvent !== event
                );
            } else {
                return [...prevSelectedEvents, event];
            }
        });
    };

    const handleChannelSelected = (channel) => {
        setSelectedChannels((prevSelectedChannels) => {
            if (prevSelectedChannels.includes(channel.id)) {
                return prevSelectedChannels.filter(
                    (selectedChannel) => selectedChannel !== channel.id
                );
            } else {
                return [...prevSelectedChannels, channel.id];
            }
        });
    };

    const handleOutputSelected = (output) => {
        setSelectedOutputIds((prevSelected) => {
            if (prevSelected.includes(output.id)) {
                return prevSelected.filter((id) => id !== output.id);
            } else {
                return [...prevSelected, output.id];
            }
        });
    };

    // Event groups come from the login response (group/groupLabel per event) - e.g.
    // "optical" (Camera detection), "thermal_perimeter" (behaviorRule), "inputs" (IO).
    const eventGroups = Array.from(
        new Map(availableEvents.map((event) => [event.group, event.groupLabel || event.group])).entries()
    ).map(([group, groupLabel]) => ({ group, groupLabel }));

    // Physical inputs are wired to the NVR itself, not to a camera channel - unlike optical/
    // thermal events, they're not scoped to specific channels at all.
    const NO_CHANNEL_GROUPS = new Set(["inputs"]);
    const requiresChannelSelection = selectedEvents.some((event) => !NO_CHANNEL_GROUPS.has(event.group));

    const thermalChannelIds = new Set(availableThermalChannels.map((channel) => channel.id));
    const includesThermalEvent = selectedEvents.some((event) => event.group === "thermal_perimeter");
    const includesNonThermalEvent = selectedEvents.some((event) => event.group !== "thermal_perimeter" && !NO_CHANNEL_GROUPS.has(event.group));
    const channelChoices = availableChannels.filter((channel) => {
        const isThermal = thermalChannelIds.has(channel.id);
        return (isThermal && includesThermalEvent) || (!isThermal && includesNonThermalEvent);
    });

    useEffect(() => {
        const fetchAvailableEvents = async () => {
            setEventsLoading(true);
            setEventsError("");

            const loginBody = {
                protocol: "http",
                hostName: existingIP,
                port: existingPort,
                username: integration.username,
                password: integration.password,
                userID
            };

            try {
                const loginResponse = await hikvisionLogin(apiData, loginBody, dispatch);
                if (loginResponse.success) {
                    const renamedEvents = loginResponse.data
                        .filter(event => !HIDDEN_EVENT_TYPES.has(event.eventType))
                        .map(event => ({
                            ...event,
                            displayName: event.label ?? EVENT_NAME_MAP[event.eventType] ?? event.eventType,
                            group: event.group ?? "other",
                            groupLabel: event.groupLabel ?? "Other events"
                        }));
                    setAvailableEvents(renamedEvents);
                    setSelectedEvents(renamedEvents.filter(event => controlledEventTypes.has(event.eventType)));

                    setAvailableChannels(loginResponse.channels);
                    setSelectedChannels(loginResponse.channels
                        .filter(channel => controlledChannelIds.has(channel.id))
                        .map(channel => channel.id));

                    setAvailableThermalChannels(loginResponse.thermalChannels ?? []);

                    setAvailableIOOutputs(loginResponse.ioOutputs ?? []);
                    setSelectedOutputIds((loginResponse.ioOutputs ?? [])
                        .filter(output => controlledOutputIds.has(String(output.id)))
                        .map(output => output.id));
                } else {
                    setEventsError("Failed to log in to Hikvision to retrieve controlled events");
                }
            } catch {
                setEventsError("Failed to log in to Hikvision to retrieve controlled events");
            } finally {
                setEventsLoading(false);
            }
        };

        fetchAvailableEvents();
    }, []);

    const [ipAddressState, dispatchIPAddressState] = useReducer(TextReducer, {
        value: existingIP ?? '',
        isValid: true
    });

    const [portState, dispatchPortState] = useReducer(TextReducer, {
        value: existingPort ?? '',
        isValid: true
    });

    const [usernameState, dispatchUsernameState] = useReducer(TextReducer, {
        value: username ?? '',
        isValid: null
    });

    const [passwordState, dispatchPasswordState] = useReducer(TextReducer, {
        value: password ?? '',
        isValid: null
    });

    const submitHandler = async (event) => {
        event.preventDefault();
        setStep('saving');

        // Re-authenticate with the (possibly edited) credentials before touching the existing
        // link, so a bad new IP/username/password doesn't leave the pin unlinked.
        const loginBody = {
            protocol: "http",
            hostName: ipAddressState.value,
            port: portState.value,
            username: usernameState.value,
            password: passwordState.value,
            userID
        };

        const loginResponse = await hikvisionLogin(apiData, loginBody, dispatch);
        if (!loginResponse.success) {
            setFailReason("Failed to log in to Hikvision with the details provided, please check them and try again");
            setStep('fail');
            return;
        }

        const unlinkResponse = await unlinkHikvision(apiData, siteUUID, pin, dispatch);
        if (!unlinkResponse.success) {
            setFailReason("Failed to unlink the existing Hikvision, please try again");
            setStep('fail');
            return;
        }

        const relinkBody = {
            pinNumber: pin,
            siteUUID,
            userID,
            ipAndPort: `${ipAddressState.value}:${portState.value}`,
            eventsToHandle: controlMode === 'relay'
                ? [{ controlLevel: "alarm_output", outputIds: selectedOutputIds }]
                : selectedEvents,
            channelIds: (controlMode === 'relay' || !requiresChannelSelection) ? null : selectedChannels
        };

        const relinkResponse = await addHikvision(apiData, relinkBody, dispatch);
        if (relinkResponse.success) {
            setStep('success');
        } else {
            setFailReason("The existing Hikvision was unlinked but relinking with the new details failed - please add it again manually");
            setStep('fail');
        }
    };

    if (step === 'success') {
        return (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <h3 style={{ textAlign: "center", color: "#858585" }}>Hikvision updated successfully!</h3>
                <Lottie animationData={success} loop={false} style={{ width: 250, height: 250, margin: "0px" }} />
                <Button
                    onClick={() => { refreshDevices?.(); onClose?.(); }}
                    style={{ width: "100%" }}
                    severity='secondary'
                    label='Close'
                    icon="pi pi-times"
                />
            </div>
        );
    }

    if (step === 'fail') {
        return (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <Lottie animationData={fail} loop={false} style={{ width: 150, height: 150, margin: "0px" }} />
                <h3 style={{ textAlign: "center", color: "#6e6e6eff" }}>{failReason}</h3>
                <Button icon="pi pi-times" onClick={() => setStep('form')} label="Try again" severity='secondary' />
            </div>
        );
    }

    return (
        <form onSubmit={submitHandler} className={styles["container1"]}>
            <h3 style={{ color: "#6e6e6e" }}>Edit Hikvision integration</h3>
            <br />
            <small style={{ color: "#6e6e6e" }}>Please do not include 'https://' or the port in the IP Address field</small>
            <br />

            <input
                placeholder="IP Address"
                value={ipAddressState.value}
                onChange={(e) => dispatchIPAddressState({ type: 'USER_INPUT', val: e.target.value })}
                type="text"
                className={styles.inputField}
                required
            />

            <input
                placeholder="HTTP Port"
                value={portState.value}
                onChange={(e) => dispatchPortState({ type: 'USER_INPUT', val: e.target.value })}
                type="text"
                className={styles.inputField}
                required
            />

            <input
                placeholder="Username"
                value={usernameState.value}
                onChange={(e) => dispatchUsernameState({ type: 'USER_INPUT', val: e.target.value })}
                type="text"
                className={styles.inputField}
                required
            />

            <PasswordInput
                placeholder="Password"
                value={passwordState.value}
                onChange={(e) => dispatchPasswordState({ type: 'USER_INPUT', val: e.target.value })}
                className={styles.inputField}
                required
            />

            

            <div style={{ width: "100%", marginTop: "5px", textAlign: "left" }}>
                <h4 style={{ color: "#6e6e6e" }}>What would you like to control?</h4>
                {eventsLoading && <p style={{ color: "#6e6e6e" }}>Logging in to Hikvision to retrieve options...</p>}
                {!eventsLoading && eventsError && <p className={styles.errorMessage}>{eventsError}</p>}
                {!eventsLoading && !eventsError && (
                    <>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
                            <Button
                                type="button"
                                severity="secondary"
                                outlined={controlMode !== 'events'}
                                label="Events"
                                style={{ width: "200px" }}
                                onClick={() => setControlMode('events')}
                            />
                            {availableIOOutputs.length > 0 && (
                                <Button
                                    type="button"
                                    severity="secondary"
                                    outlined={controlMode !== 'relay'}
                                    label="Alarm Outputs"
                                    style={{ width: "200px" }}
                                    onClick={() => setControlMode('relay')}
                                />
                            )}
                        </div>

                        {controlMode === 'events' && (
                            <>
                                {eventGroups.map(({ group, groupLabel }) => (
                                    <div key={group} style={{ marginTop: "15px" }}>
                                        <p style={{ color: "#9e9e9e", fontSize: "12px", fontWeight: 600, margin: "0 0 8px 0" }}>{groupLabel}</p>
                                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
                                            {availableEvents.filter(event => event.group === group).map((event, index) => (
                                                <SelectableListRow
                                                    key={index}
                                                    selected={selectedEvents.includes(event)}
                                                    label={event.displayName}
                                                    onClick={() => handleEventSelected(event)}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ))}

                                {requiresChannelSelection && (
                                    <div style={{ marginTop: "15px" }}>
                                        <p style={{ color: "#9e9e9e", fontSize: "12px", fontWeight: 600, margin: "0 0 8px 0" }}>Channels to control</p>
                                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
                                            {channelChoices.map((channel, index) => (
                                                <SelectableListRow
                                                    key={index}
                                                    selected={selectedChannels.includes(channel.id)}
                                                    label={channel.name}
                                                    onClick={() => handleChannelSelected(channel)}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {controlMode === 'relay' && (
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", marginTop: "15px" }}>
                                {availableIOOutputs.map((output, index) => (
                                    <SelectableListRow
                                        key={index}
                                        selected={selectedOutputIds.includes(output.id)}
                                        label={output.ioType ? `${output.id} (${output.ioType})` : output.id}
                                        onClick={() => handleOutputSelected(output)}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
<br/>
            <Button
                icon="pi pi-save"
                severity="success"
                type="submit"
                disabled={step === 'saving'}
                label={step === 'saving' ? "Saving..." : "Save"}
            />
        </form>
    );
}

export default HikvisionEditForm;
